/**
 * Copyright (c) 2026 Firstsource. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Firstsource.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Firstsource.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: [Developer Name / Date]
 */
import React from 'react';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

interface DeboardUserModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isButtonLoading?: boolean;
}

const DeboardUserModal: React.FC<DeboardUserModalProps> = ({ open, onCancel, onConfirm, isButtonLoading = false }) => {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      aria-labelledby="deboard-user-modal-title"
      aria-describedby="deboard-user-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
          width: '460px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CloseIcon onClick={onCancel} sx={{ color: '#6B7280', cursor: 'pointer' }} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: '#FDECEA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <PersonRemoveAlt1Icon sx={{ color: '#E57373', fontSize: 28 }} />
          </Box>
          <Typography
            id="deboard-user-modal-title"
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 700,
              fontSize: '18px',
              lineHeight: '24px',
              color: '#111827',
              textAlign: 'center',
            }}
          >
            Deboard User from Company?
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography
          id="deboard-user-modal-description"
          sx={{
            fontFamily: 'Montserrat',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
            color: '#374151',
            mb: 2,
          }}
        >
          Are you sure you want to deboard this user from the company?
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: '#FFF8E1',
            border: '1px solid #FFD54F',
            borderRadius: '8px',
            p: 1.5,
            mb: 1.5,
          }}
        >
          <WarningAmberIcon sx={{ color: '#F59E0B', fontSize: 20, mt: '2px', flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '20px',
              color: '#78350F',
            }}
          >
            The user will lose access to all projects associated with this company and will no longer be able to view or work on them.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            p: 1.5,
            mb: 3,
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#6B7280', fontSize: 20, mt: '2px', flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '20px',
              color: '#374151',
            }}
          >
            This action can be reversed by re-adding the user to the company.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              minHeight: '40px',
              borderRadius: '8px',
              px: 3,
              borderColor: '#D1D5DB',
              color: '#374151',
              textTransform: 'none',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isButtonLoading}
            startIcon={isButtonLoading ? <CircularProgress size={17} sx={{ color: '#fff' }} /> : <DeleteIcon />}
            sx={{
              minHeight: '40px',
              backgroundColor: '#C00F00',
              borderRadius: '8px',
              px: 3,
              textTransform: 'none',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#A00D00',
              },
              '&.Mui-disabled': {
                backgroundColor: '#F87171',
                color: '#fff',
              },
            }}
          >
            Confirm Deboard
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeboardUserModal;
